import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

function getCategorySlug(categoryName: string): string {
  const cat = categories.find((c) => c.name === categoryName);
  return cat ? cat.slug : 'xuanhuan';
}

interface ComicCardProps {
  id: number;
  title: string;
  author: string;
  category: string;
  cover: string;
  chapters: number;
  rating: number;
  views: string;
  status: string;
  isHot?: boolean;
  updatedAt?: string;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ComicCard({
  id,
  title,
  author,
  category,
  cover,
  chapters,
  rating,
  views,
  status,
  isHot,
  updatedAt,
  variant = 'default',
}: ComicCardProps) {
  const categorySlug = getCategorySlug(category);
  const linkTo = `/category/${categorySlug}`;

  if (variant === 'featured') {
    return (
      <Link
        to={linkTo}
        className="group block relative rounded-lg overflow-hidden bg-foreground-950 cursor-pointer h-full min-h-[360px]"
        data-product-shop=""
      >
        <img
          src={cover}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover object-top opacity-60 group-hover:opacity-70 transition-opacity duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/40 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          {isHot && (
            <span className="inline-block px-2.5 py-0.5 text-xs font-medium text-background-50 bg-primary-500 rounded-full mb-3">
              热门推荐
            </span>
          )}
          <h3 className="text-xl font-heading text-background-50 mb-1">{title}</h3>
          <p className="text-sm text-foreground-300 mb-3">{author} · {category}</p>
          <div className="flex items-center gap-4 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-500"></i>
              {rating}
            </span>
            <span>{chapters}话</span>
            <span>{views}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={linkTo}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
        data-product-shop=""
      >
        <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
          <img src={cover} alt={title} className="w-full h-full object-cover object-top" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <h4 className="text-sm font-medium text-foreground-900 truncate">{title}</h4>
            <p className="text-xs text-foreground-500 mt-0.5">{author}</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="text-accent-500">★ {rating}</span>
            <span>{chapters}话</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={linkTo}
      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-300 cursor-pointer"
      data-product-shop=""
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-background-200">
        <img
          src={cover}
          alt={title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {isHot && (
          <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-medium text-background-50 bg-primary-500 rounded-full">
            HOT
          </span>
        )}
        <div className="absolute top-2 right-2 px-2 py-0.5 text-xs font-medium text-background-50 bg-foreground-950/70 rounded-full">
          {status}
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-medium text-foreground-900 truncate">{title}</h3>
        <p className="text-xs text-foreground-500 mt-1">{author}</p>
        <div className="mt-2 flex items-center justify-between">
          <span className="flex items-center gap-1 text-xs text-accent-500 font-medium">
            <i className="ri-star-fill"></i>
            {rating}
          </span>
          <span className="text-xs text-foreground-400">{chapters}话</span>
        </div>
      </div>
    </Link>
  );
}